import {
  type JSX,
  type MouseEventHandler,
  type TouchEventHandler,
  cloneElement,
  useCallback,
  useMemo,
  useState,
} from "react";

import type { ProgressRootProps as ArkProgressRootProps } from "@ark-ui/react/progress";
import { Progress as ArkProgress, useProgress } from "@ark-ui/react/progress";
import {
  cx,
  type RecipeVariantProps,
  sva,
} from "@mobsuccess-devops/styled-system/css";
import { hstack } from "@mobsuccess-devops/styled-system/patterns";

import { composition } from "../../../features/utils/composition";

import { ProgressValueText } from "./ProgressValueText";

export type ProgressSelectValueDetails = {
  value: number;
};

type LinearProgressProps = Omit<
  ArkProgressRootProps,
  "orientation" | "asChild"
> &
  RecipeVariantProps<typeof styles> & {
    clickable?: boolean;
    renderThumbLabel?: (value: number) => React.ReactNode;
    onSelectValue?: (details: ProgressSelectValueDetails) => void;
  };

export function LinearProgress({
  size,
  palette,
  clickable,
  children,
  className,
  onSelectValue,
  renderThumbLabel,
  ...props
}: LinearProgressProps): JSX.Element {
  const [hoverPosition, setHoverPosition] = useState<null | number>(null);
  const classes = styles({ palette, size, clickable });

  const machine = useProgress(props);

  const { valueText, label } = composition.split(children, {
    label: composition.single(ArkProgress.Label),
    valueText: composition.single(ProgressValueText),
  });

  const dynamicStyles = useMemo(() => {
    if (hoverPosition === null) {
      return {
        thumb: {
          display: "none",
        },
        shadowRange: {
          display: "none",
        },
      };
    }

    // add the x offset to the thumb
    return {
      thumb: {
        left: `${hoverPosition}%`,
      },
      shadowRange: {
        width: `${hoverPosition}%`,
      },
    };
  }, [hoverPosition]);

  const calculatePosition = useCallback(
    (clientX: number, element: HTMLElement) => {
      const x = clientX - element.getBoundingClientRect().left;
      const percentage = (x / element.clientWidth) * 100;
      return Math.max(0, Math.min(100, percentage));
    },
    [],
  );

  const handleTrackMouse = useCallback<MouseEventHandler<HTMLDivElement>>(
    (event) => {
      // if the mouse is not or no longer over the track, set hoverPosition to null
      // base yourselef on the mouse event type
      if (event.type === "mouseleave") {
        setHoverPosition(null);
        return;
      }

      const percentage = calculatePosition(event.clientX, event.currentTarget);
      setHoverPosition(percentage);
    },
    [calculatePosition],
  );

  const handleTrackTouch = useCallback<TouchEventHandler<HTMLDivElement>>(
    (event) => {
      event.preventDefault();

      if (event.type === "touchend") {
        // Trigger value selection on touch release
        if (onSelectValue && hoverPosition !== null) {
          onSelectValue({ value: hoverPosition });
        }
        setHoverPosition(null);
        return;
      }

      const touch = event.touches[0];
      if (!touch) {
        return;
      }

      const percentage = calculatePosition(touch.clientX, event.currentTarget);
      setHoverPosition(percentage);
    },
    [calculatePosition, hoverPosition, onSelectValue],
  );

  const handleSelectValue = useCallback(() => {
    if (!onSelectValue || hoverPosition === null) {
      return;
    }
    onSelectValue({ value: hoverPosition });
  }, [hoverPosition, onSelectValue]);

  return (
    <ArkProgress.RootProvider
      value={machine}
      className={cx(classes.root, className)}
    >
      {label}
      <ArkProgress.Track
        className={classes.track}
        onMouseMove={handleTrackMouse}
        onMouseLeave={handleTrackMouse}
        onTouchStart={handleTrackTouch}
        onTouchMove={handleTrackTouch}
        onTouchEnd={handleTrackTouch}
      >
        <ArkProgress.Range
          className={cx(classes.shadowRange)}
          hidden={hoverPosition === null}
          style={dynamicStyles.shadowRange}
        />
        <ArkProgress.Range className={classes.range} />
        <div
          className={classes.thumb}
          style={dynamicStyles.thumb}
          onClick={clickable ? handleSelectValue : undefined}
        />
        {renderThumbLabel && hoverPosition !== null && (
          <div className={classes.thumbLabel} style={dynamicStyles.thumb}>
            {renderThumbLabel(hoverPosition)}
          </div>
        )}
      </ArkProgress.Track>
      {valueText &&
        cloneElement(valueText, {
          _type: "linear",
          className: classes.valueText,
        })}
    </ArkProgress.RootProvider>
  );
}

const styles = sva({
  slots: [
    "root",
    "label",
    "valueText",
    "track",
    "range",
    "thumb",
    "thumbLabel",
    "shadowRange",
  ],
  base: {
    root: hstack.raw(),
    track: {
      flex: 1,
      position: "relative",
      borderRadius: "full",
      backgroundColor: "interface.200",
    },
    range: {
      height: "full",
      position: "absolute",
      top: 0,
      left: 0,
      borderRadius: "full",
      backgroundColor: "colorPalette.400",
    },
    shadowRange: {
      top: 0,
      left: 0,
      position: "absolute",
      height: "full",
      borderRadius: "full",
      backgroundColor:
        "color-mix(in srgb, token(colors.colorPalette.400) 40%, white 60%)",
    },
    valueText: {
      textStyle: "muted",
      fontWeight: "bold",
      padding: "xs",
      color: "white",
      display: "flex",
      backgroundColor: "colorPalette.400",
      borderRadius: "sm",
    },
    thumb: {
      width: "10px",
      top: "50%",
      height: "10px",
      cursor: "pointer",
      position: "absolute",
      borderRadius: "full",
      transform: `translate(-50%, -50%)`,
      backgroundColor: "colorPalette.400",
    },
    thumbLabel: {
      position: "absolute",
      bottom: "50%",
      transform: "translate(-50%, -50%)",
    },
  },
  variants: {
    palette: {
      primary: { root: { colorPalette: "primary" } },
      secondary: { root: { colorPalette: "secondary" } },
      interface: { root: { colorPalette: "interface" } },
      success: { root: { colorPalette: "success" } },
      danger: { root: { colorPalette: "danger" } },
      warning: { root: { colorPalette: "warning" } },
      info: { root: { colorPalette: "info" } },
    },
    size: {
      xs: {
        track: {
          height: "2px",
        },
      },
      sm: {
        track: {
          height: "6px",
        },
      },
      md: {
        track: {
          height: "10px",
        },
      },
      lg: {
        track: {
          height: "16px",
        },
      },
    },
    clickable: {
      false: {
        thumb: {
          display: "none",
        },
        shadowRange: {
          display: "none",
        },
      },
    },
  },
  defaultVariants: {
    size: "md",
    clickable: false,
    palette: "interface",
  },
});
